import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Config } from '../../config';

// Link policy. Kept here rather than inline in the request so the copy shown to
// the user cannot drift from what is actually asked for — the loading state
// used to claim "Downloads: 1" while requesting 5.
const LINK_EXPIRY_HOURS = 24;
const LINK_MAX_DOWNLOADS = 5;

const formatDateTime = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';
  // en-GB: day before month, so the date is never ambiguous between locales.
  return date.toLocaleString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const pluralDownloads = (n) => (n === 1 ? '1 download' : `${n} downloads`);

const GenerateDownloadLinkModal = ({ agent, onClose, onLinkGenerated }) => {
  const [isGenerating, setIsGenerating] = useState(true); // Start as generating
  const [generatedLink, setGeneratedLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [error, setError] = useState(null);

  // Auto-generate link once on mount
  useEffect(() => {
    let cancelled = false;
    const generateLink = async () => {
      setIsGenerating(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${Config.APIBaseURL}/agent/${agent.id}/download-link`,
          {
            customer_email: null,
            customer_name: null,
            expires_hours: LINK_EXPIRY_HOURS,
            max_downloads: LINK_MAX_DOWNLOADS,
            send_email: false,
            notes: null
          },
          {
            headers: { 'access_token': `Bearer ${token}` }
          }
        );

        if (!cancelled) {
          setGeneratedLink(response.data);
          if (onLinkGenerated) onLinkGenerated(response.data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error generating download link:', err);
          setError(err.response?.data?.detail || err.response?.data?.message || err.message);
        }
      } finally {
        if (!cancelled) setIsGenerating(false);
      }
    };

    generateLink();
    return () => { cancelled = true; };
  }, [agent.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const maxDownloads = generatedLink?.max_downloads ?? LINK_MAX_DOWNLOADS;

  const title = isGenerating
    ? 'Generating Download Link'
    : error
      ? 'Could Not Generate Link'
      : 'Download Link Ready';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={!isGenerating ? onClose : undefined}></div>

      {/* Modal Content */}
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-auto rounded-2xl bg-slate-900/90 shadow-2xl border border-slate-800 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            <p className="text-sm text-slate-400 mt-0.5">{agent.agent_name}</p>
          </div>
          <button
            onClick={onClose}
            disabled={isGenerating}
            className="text-slate-400 hover:text-white disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {isGenerating ? (
          <div className="flex flex-col items-center justify-center py-12">
            <svg className="animate-spin h-12 w-12 text-indigo-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-slate-300 text-sm">Creating a one-off download link…</p>
            <p className="text-slate-500 text-xs mt-2">
              Valid for {LINK_EXPIRY_HOURS} hours · {pluralDownloads(LINK_MAX_DOWNLOADS)} maximum
            </p>
          </div>
        ) : error ? (
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-red-950/30 border border-red-500/50">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-red-400 mb-1">The link could not be created</h4>
                  <p className="text-sm text-slate-300">{error}</p>
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-6 py-2 rounded-lg text-sm font-medium bg-slate-700 text-white hover:bg-slate-600"
              >
                Close
              </button>
            </div>
          </div>
        ) : generatedLink ? (
          <>
            <div className="space-y-4">
              {/* Download Link */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Download link</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={generatedLink.download_url}
                    className="flex-1 rounded-md bg-slate-950/70 border border-slate-800 p-3 text-indigo-400 text-sm font-mono select-all focus:border-indigo-600 focus:outline-none"
                    onClick={(e) => e.target.select()}
                  />
                  <button
                    onClick={() => copyToClipboard(generatedLink.download_url)}
                    className={`px-4 py-3 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                      linkCopied
                        ? 'bg-emerald-600 text-white'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}
                  >
                    {linkCopied ? (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Copied
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Link Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-slate-950/50 border border-slate-800">
                  <div className="text-xs text-slate-500 mb-1">Expires</div>
                  <div className="text-sm font-medium text-slate-300">
                    {formatDateTime(generatedLink.expires_at)}
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-slate-950/50 border border-slate-800">
                  <div className="text-xs text-slate-500 mb-1">Download limit</div>
                  <div className="text-sm font-medium text-slate-300">
                    {pluralDownloads(maxDownloads)}
                  </div>
                </div>
              </div>

              {/* Security Notice */}
              <div className="p-3 rounded-lg bg-amber-950/20 border border-amber-500/30">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="text-xs text-slate-300 leading-relaxed">
                    <strong className="text-amber-400">Unauthenticated link.</strong> Anyone holding it can
                    download the agent VM — share it only with authorised recipients. It stops working after{' '}
                    {pluralDownloads(maxDownloads)} or on {formatDateTime(generatedLink.expires_at)},
                    whichever comes first.
                  </div>
                </div>
              </div>
            </div>

            {/* Close Button */}
            <div className="flex justify-end mt-6 pt-4 border-t border-slate-800">
              <button
                onClick={onClose}
                className="px-6 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Done
              </button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default GenerateDownloadLinkModal;
